<template>
  <div v-loading="loading" class="page">
    <el-row :gutter="16">
      <el-col :xs="24" :md="13">
        <el-card shadow="never" class="panel mb-16px">
          <template #header>收款账户</template>
          <div class="bank-card">
            <div class="bank-card__sub">当前结算打款至</div>
            <div class="bank-card__no">{{ mask(form.bankAccountNo) }}</div>
            <div class="bank-card__meta">
              <span>{{ form.bankName || '未设置开户行' }}</span>
              <span>{{ form.bankAccountName || '-' }}</span>
            </div>
          </div>
          <el-alert
            v-if="form.bankChangeEffectiveTime"
            class="mt-12px"
            type="warning"
            show-icon
            :closable="false"
            :title="`待生效账户将于 ${form.bankChangeEffectiveTime} 生效：${form.pendingBankName || ''} ${mask(form.pendingBankAccountNo)}`"
          />
          <div class="section-title">变更收款账户（短信校验 · T+1 生效）</div>
          <el-form label-width="90px" class="mt-8px">
            <el-form-item label="开户行">
              <el-input v-model="edit.bankName" placeholder="请输入开户行" />
            </el-form-item>
            <el-form-item label="账号">
              <el-input v-model="edit.bankAccountNo" placeholder="请输入银行账号" />
            </el-form-item>
            <el-form-item label="户名">
              <el-input v-model="edit.bankAccountName" placeholder="请输入户名" />
            </el-form-item>
            <el-form-item label="短信验证码">
              <div class="sms-row">
                <el-input v-model="smsCode" placeholder="变更账户必填" />
                <el-button :disabled="smsCountdown > 0" @click="sendSms">
                  {{ smsCountdown > 0 ? `${smsCountdown}s` : '获取验证码' }}
                </el-button>
              </div>
            </el-form-item>
            <el-button class="submit-btn" type="primary" :loading="saving" @click="save">
              提交变更
            </el-button>
          </el-form>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="11">
        <el-card shadow="never" class="panel">
          <template #header>档案信息</template>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="名称">{{ form.name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系人">
              {{ form.contactName || '-' }} / {{ form.contactMobile || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="区域">
              {{ form.regionName || '-' }}{{ form.regionExclusive ? '（独占）' : '' }}
            </el-descriptions-item>
            <el-descriptions-item label="层级">
              {{ form.level === 2 ? '二级代理' : '一级代理' }}
            </el-descriptions-item>
            <el-descriptions-item label="保证金">
              ¥{{ fen2yuan(form.depositAmount) }}
            </el-descriptions-item>
            <el-descriptions-item v-if="form.level !== 2" label="默认让渡比例">
              {{ ((form.subRate || 0) * 100).toFixed(0) }}%
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { getPortalAccount, sendPortalBankSms, updatePortalAccount } from '@/api/agent/portal'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'AgentPortalAccount' })

const loading = ref(false)
const saving = ref(false)
const smsCode = ref('')
const smsCountdown = ref(0)
const form = reactive<any>({})
const edit = reactive({ bankName: '', bankAccountNo: '', bankAccountName: '' })

const mask = (no?: string) => (!no ? '-' : no.replace(/(\d{4})\d+(\d{4})/, '$1 **** **** $2'))
const fen2yuan = (fen?: number) =>
  fen == null
    ? '0.00'
    : (Number(fen) / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

let timer: any
const sendSms = async () => {
  await sendPortalBankSms()
  ElMessage.success('验证码已发送至联系人手机')
  smsCountdown.value = 60
  timer = setInterval(() => {
    smsCountdown.value -= 1
    if (smsCountdown.value <= 0) clearInterval(timer)
  }, 1000)
}

const fillEdit = () => {
  edit.bankName = form.bankName || ''
  edit.bankAccountNo = form.bankAccountNo || ''
  edit.bankAccountName = form.bankAccountName || ''
}

onMounted(async () => {
  loading.value = true
  try {
    Object.assign(form, (await getPortalAccount()) || {})
    fillEdit()
  } finally {
    loading.value = false
  }
})

const save = async () => {
  if (!edit.bankName || !edit.bankAccountNo || !edit.bankAccountName) {
    ElMessage.warning('请完整填写开户行、账号、户名')
    return
  }
  if (!smsCode.value) {
    ElMessage.warning('请填写短信验证码')
    return
  }
  saving.value = true
  try {
    await updatePortalAccount({
      bankName: edit.bankName,
      bankAccountNo: edit.bankAccountNo,
      bankAccountName: edit.bankAccountName,
      smsCode: smsCode.value
    })
    ElMessage.success('已提交，将于 T+1 生效')
    Object.assign(form, (await getPortalAccount()) || {})
    fillEdit()
    smsCode.value = ''
  } finally {
    saving.value = false
  }
}

onBeforeUnmount(() => timer && clearInterval(timer))
</script>

<style scoped>
.bank-card {
  background: linear-gradient(135deg, #083a34, #0e9c8d);
  border-radius: 14px;
  color: #fff;
  padding: 22px 20px;
}
.bank-card__sub {
  font-size: 12px;
  opacity: 0.8;
}
.bank-card__no {
  font-size: 22px;
  letter-spacing: 2px;
  margin: 12px 0;
  font-family: Consolas, monospace;
  font-weight: 600;
}
.bank-card__meta {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  opacity: 0.9;
  gap: 12px;
}
.section-title {
  margin-top: 18px;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.sms-row {
  display: flex;
  gap: 8px;
  width: 100%;
}
.submit-btn {
  background: #0e9c8d;
  border-color: #0e9c8d;
}
.submit-btn:hover {
  background: #0b8578;
  border-color: #0b8578;
}
</style>
